import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Flame, Loader2, LogOut, MessageSquareWarning, ShieldCheck, UserPlus } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import BrandMark from "@/components/BrandMark";
import WaitlistCard from "@/components/admin/WaitlistCard";
import LeadCard from "@/components/admin/LeadCard";
import WinbackStats from "@/components/admin/WinbackStats";

function LoginForm({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      onSuccess(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#07090D] flex items-center justify-center px-4" data-testid="admin-login-view">
      <motion.form
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        onSubmit={submit}
        className="w-full max-w-sm glass-panel rounded-md p-8"
      >
        <div className="flex items-center gap-3 mb-8">
          <span className="w-1 h-8 bg-amber-500 rounded-sm" />
          <BrandMark tag="Underwriting" />
        </div>
        <label className="block text-xs font-mono-d tracking-[0.2em] uppercase text-zinc-500 mb-2" htmlFor="admin-email">Email</label>
        <input
          id="admin-email"
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          data-testid="admin-email-input"
          className="w-full bg-[#07090D] border border-white/15 rounded-sm px-4 py-3 text-sm text-zinc-100 focus:outline-none focus:border-amber-500/60 mb-5 transition-colors"
        />
        <label className="block text-xs font-mono-d tracking-[0.2em] uppercase text-zinc-500 mb-2" htmlFor="admin-password">Password</label>
        <input
          id="admin-password"
          type="password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          data-testid="admin-password-input"
          className="w-full bg-[#07090D] border border-white/15 rounded-sm px-4 py-3 text-sm text-zinc-100 focus:outline-none focus:border-amber-500/60 mb-8 transition-colors"
        />
        <button
          type="submit"
          disabled={loading}
          data-testid="admin-login-button"
          className="w-full inline-flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-400 disabled:opacity-40 text-[#07090D] font-semibold text-sm px-6 py-3.5 rounded-sm transition-all duration-300 active:scale-[0.99]"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
          Access lead vault
        </button>
      </motion.form>
    </div>
  );
}

const isHot = (l) => l.hot_file && l.status !== "declined";

export default function AdminPage() {
  const [user, setUser] = useState(undefined);
  const [leads, setLeads] = useState(null);
  const [waitlist, setWaitlist] = useState([]);
  const [config, setConfig] = useState(null);
  const [stats, setStats] = useState(null);
  const [statusBusy, setStatusBusy] = useState(null);
  const [winbackBusy, setWinbackBusy] = useState(null);

  useEffect(() => {
    api.get("/auth/me").then(({ data }) => setUser(data)).catch(() => setUser(null));
  }, []);

  useEffect(() => {
    if (user) {
      api.get("/admin/leads").then(({ data }) => setLeads(data)).catch((e) => toast.error(formatApiError(e)));
      api.get("/admin/waitlist").then(({ data }) => setWaitlist(data)).catch(() => {});
      api.get("/admin/config").then(({ data }) => setConfig(data)).catch(() => {});
      api.get("/admin/winback/stats").then(({ data }) => setStats(data)).catch(() => {});
    }
  }, [user]);

  const sendNextWinback = async (entryId) => {
    setWinbackBusy(entryId);
    try {
      const { data } = await api.post(`/admin/waitlist/${entryId}/send`);
      setWaitlist((prev) => prev.map((w) => (w.id === entryId ? data : w)));
      api.get("/admin/winback/stats").then((r) => setStats(r.data)).catch(() => {});
      toast.success(`Win-back email ${data.sequence_step} sent.`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setWinbackBusy(null);
    }
  };

  const download = async (file) => {
    try {
      const res = await api.get(`/admin/files/${file.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.original_filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const setStatus = async (leadId, status) => {
    setStatusBusy(leadId + status);
    try {
      await api.post(`/admin/leads/${leadId}/status`, { status });
      setLeads((prev) => prev.map((l) => (l.id === leadId ? { ...l, status } : l)));
      toast.success(`Lead ${status}.`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setStatusBusy(null);
    }
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(null);
    setLeads(null);
  };

  if (user === undefined) {
    return (
      <div className="min-h-screen bg-[#07090D] flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-amber-400 animate-spin" />
      </div>
    );
  }
  if (!user) return <LoginForm onSuccess={setUser} />;

  const hotLeads = (leads || []).filter(isHot).sort((a, b) => b.monthly_revenue - a.monthly_revenue);
  const pipeline = (leads || []).filter((l) => !isHot(l));
  const cardProps = { statusBusy, onStatus: setStatus, onDownload: download };

  return (
    <div className="min-h-screen bg-[#07090D] text-zinc-100" data-testid="admin-dashboard">
      <header className="border-b border-white/10 bg-[#0A0D12]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="w-1 h-7 bg-amber-500 rounded-sm" />
            <BrandMark compact tag="Lead Vault" />
            <span className="hidden sm:inline text-xs font-mono-d tracking-[0.15em] uppercase text-zinc-600 ml-3">{user.email}</span>
          </div>
          <button
            onClick={logout}
            data-testid="admin-logout-button"
            className="inline-flex items-center gap-2 text-xs font-mono-d tracking-[0.15em] uppercase text-zinc-400 hover:text-amber-400 transition-colors"
          >
            <LogOut className="w-3.5 h-3.5" /> Logout
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {config && !config.sms_configured && (
          <div
            className="mb-8 flex items-start gap-3 border border-amber-500/30 bg-amber-500/[0.06] rounded-sm px-4 py-3"
            data-testid="sms-not-configured-banner"
          >
            <MessageSquareWarning className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
            <p className="text-xs leading-relaxed text-zinc-300">
              <span className="text-amber-400 font-semibold">SMS hot-file alerts are armed but waiting on your Twilio sending number.</span>{" "}
              Files at ${Number(config.sms_high_revenue_threshold).toLocaleString()}/mo+ are flagged below
              {config.alert_phone ? ` and will text ${config.alert_phone}` : ""} once{" "}
              {(config.sms_missing || []).join(", ")} is set.
            </p>
          </div>
        )}

        {!leads ? (
          <div className="flex justify-center py-24">
            <Loader2 className="w-6 h-6 text-amber-400 animate-spin" />
          </div>
        ) : leads.length === 0 ? (
          <p className="text-center text-zinc-500 font-editorial italic text-lg py-24" data-testid="admin-empty-state">
            No inbound files yet. The funnel is listening.
          </p>
        ) : (
          <>
            {hotLeads.length > 0 && (
              <section className="mb-12" data-testid="hot-file-lane">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
                  <div className="flex items-center gap-2.5">
                    <Flame className="w-4 h-4 text-orange-400" />
                    <h2 className="font-display text-lg font-medium tracking-tight text-zinc-100">
                      Hot files · priority lane <span className="text-xs font-mono-d text-zinc-500 ml-2">{hotLeads.length}</span>
                    </h2>
                  </div>
                  <p className="text-xs font-mono-d text-zinc-500">
                    ${Number(config?.sms_high_revenue_threshold || 50000).toLocaleString()}/mo+ · sorted by revenue
                  </p>
                </div>
                <div className="space-y-4">
                  {hotLeads.map((lead, i) => (
                    <LeadCard key={lead.id} lead={lead} index={i} hot {...cardProps} />
                  ))}
                </div>
              </section>
            )}
            <div className="space-y-4" data-testid="admin-leads-list">
              {hotLeads.length > 0 && (
                <h2 className="font-display text-lg font-medium tracking-tight text-zinc-100 mb-5">
                  Pipeline <span className="text-xs font-mono-d text-zinc-500 ml-2">{pipeline.length}</span>
                </h2>
              )}
              {pipeline.map((lead, i) => (
                <LeadCard key={lead.id} lead={lead} index={i} {...cardProps} />
              ))}
            </div>
          </>
        )}

        <div className="mt-16" data-testid="admin-waitlist-section">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
            <div className="flex items-center gap-2.5">
              <UserPlus className="w-4 h-4 text-amber-400" />
              <h2 className="font-display text-lg font-medium tracking-tight text-zinc-100">
                Priority waitlist <span className="text-xs font-mono-d text-zinc-500 ml-2">{waitlist.length}</span>
              </h2>
            </div>
            <p className="text-xs text-zinc-500 font-mono-d" data-testid="winback-schedule-note">
              Win-back sequence · Day 0 → Day 30 → Day 90 · replies go to {config?.alert_email || "the broker inbox"}
            </p>
          </div>
          <WinbackStats stats={stats} />
          {waitlist.length === 0 ? (
            <p className="text-sm text-zinc-600 font-editorial italic">No waitlist entries yet.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {waitlist.map((w) => (
                <WaitlistCard
                  key={w.id || w.email}
                  entry={w}
                  totalSteps={config?.winback_delays_days?.length || 3}
                  busy={winbackBusy}
                  onSendNext={sendNextWinback}
                />
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
